import axios from 'axios'

// Use deployed API when running locally
const getApiBase = () => {
  if (import.meta.env.DEV) {
    return 'https://fa99a3c6.socialfi-lunatics.pages.dev/api/twitter'
  }
  return '/api/twitter'
}

// Backend API endpoints (Cloudflare Pages Functions)
const API_BASE = getApiBase()

// Environment variables
const BEARER_TOKEN = import.meta.env.VITE_TWITTER_BEARER_TOKEN
const ENABLE_REAL_DATA = import.meta.env.VITE_ENABLE_REAL_DATA === 'true'

// Types for Twitter API responses
export interface TwitterUser {
  id: string
  username: string
  name: string
  profile_image_url?: string
  verified?: boolean
  public_metrics?: {
    followers_count: number
    following_count: number
    tweet_count: number
  }
}

export interface TwitterTweet {
  id: string
  text: string
  created_at: string
  author_id: string
  public_metrics?: {
    retweet_count: number
    like_count: number
    reply_count: number
    quote_count: number
  }
  entities?: {
    mentions?: Array<{
      start: number
      end: number
      username: string
    }>
    hashtags?: Array<{
      start: number
      end: number
      tag: string
    }>
  }
}

export interface TwitterResponse {
  data?: TwitterTweet[]
  includes?: {
    users?: TwitterUser[]
  }
  meta?: {
    result_count: number
    next_token?: string
  }
}

// API client setup for our backend endpoints
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Cache for API responses
const cache = new Map<string, { data: any; timestamp: number }>()
const CACHE_DURATION = 2 * 60 * 1000 // 2 minutes for demo

// Cache helper functions
const getCacheKey = (endpoint: string, params: any) => 
  `${endpoint}_${JSON.stringify(params)}`

const getFromCache = (key: string) => {
  const cached = cache.get(key)
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data
  }
  return null
}

const setCache = (key: string, data: any) => {
  cache.set(key, { data, timestamp: Date.now() })
}

// No mock data - only real Twitter API data

// Transform backend API data to our format
const transformBackendTweetData = (tweets: any[]) => {
  return tweets.map(tweet => {
    const mentions = tweet.mentions?.map((m: any) => `@${m.username}`) || []
    const hashtags = tweet.hashtags?.map((h: any) => `#${h.tag}`) || []
    
    return {
      id: tweet.id,
      userId: tweet.author?.id || 'unknown',
      username: tweet.author?.username || 'unknown',
      name: tweet.author?.name || 'Unknown User',
      avatar: tweet.author?.profileImageUrl || '/api/placeholder/40/40',
      content: tweet.text,
      timestamp: new Date(tweet.createdAt).toISOString(),
      likes: tweet.likes || 0,
      retweets: tweet.retweets || 0,
      replies: tweet.replies || 0,
      mentions,
      hashtags,
      isVerified: tweet.author?.verified || false
    }
  })
}

// Transform Twitter API data to our format (keeping for compatibility)
const transformTweetData = (tweets: TwitterTweet[], users: TwitterUser[] = []) => {
  const userMap = new Map(users.map(user => [user.id, user]))
  
  return tweets.map(tweet => {
    const author = userMap.get(tweet.author_id)
    const mentions = tweet.entities?.mentions?.map(m => `@${m.username}`) || []
    const hashtags = tweet.entities?.hashtags?.map(h => `#${h.tag}`) || []
    
    return {
      id: tweet.id,
      userId: tweet.author_id,
      username: author?.username || 'unknown',
      name: author?.name || 'Unknown User',
      avatar: author?.profile_image_url || '/api/placeholder/40/40',
      content: tweet.text,
      timestamp: new Date(tweet.created_at).toISOString(),
      likes: tweet.public_metrics?.like_count || 0,
      retweets: tweet.public_metrics?.retweet_count || 0,
      replies: tweet.public_metrics?.reply_count || 0,
      mentions,
      hashtags,
      isVerified: author?.verified || false
    }
  })
}

// Fetch tweets mentioning @LunaticsBTC
export const fetchLunaticsMentions = async (limit = 20) => {
  console.log('Fetching Lunatics mentions...', { 
    ENABLE_REAL_DATA, 
    hasBearerToken: !!BEARER_TOKEN 
  })

  // Require real API data - no fallback to mock data
  if (!ENABLE_REAL_DATA) {
    console.log('Real data disabled')
    return []
  }

  const cacheKey = getCacheKey('mentions', { limit })
  const cached = getFromCache(cacheKey)
  if (cached) {
    console.log('Returning cached data')
    return cached
  }

  try {
    console.log('Making API call to backend...')
    const response = await apiClient.get('/mentions', {
      params: { limit: Math.min(limit, 100) }
    })

    const tweets = response.data || []
    const transformedData = transformBackendTweetData(tweets)
    
    console.log(`Fetched ${transformedData.length} real tweets`)
    setCache(cacheKey, transformedData)
    return transformedData

  } catch (error) {
    console.error('Error fetching Twitter data:', error)
    // Return empty array on error - no fake data
    return []
  }
}

// Fetch trending hashtags - real data only
export const fetchTrendingHashtags = async () => {
  if (!ENABLE_REAL_DATA) {
    return []
  }

  const cacheKey = getCacheKey('hashtags', {})
  const cached = getFromCache(cacheKey)
  if (cached) return cached

  try {
    console.log('Making API call to backend for hashtags...')
    const response = await apiClient.get('/hashtags')
    
    const hashtagData = response.data || []
    const transformedData = hashtagData.map((item: any) => ({
      tag: item.hashtag,
      count: item.count,
      change: `+${Math.floor(Math.random() * 20 + 5)}%`
    }))

    setCache(cacheKey, transformedData)
    return transformedData

  } catch (error) {
    console.error('Error fetching hashtag data:', error)
    return []
  }
}

// Fetch user metrics for social scoring
export const fetchUserSocialMetrics = async (username: string) => {
  if (!ENABLE_REAL_DATA) {
    return {
      followers: Math.floor(Math.random() * 5000) + 1000,
      engagement: Math.floor(Math.random() * 100) + 50,
      mentions: Math.floor(Math.random() * 50) + 10
    }
  }

  const cacheKey = getCacheKey('user_metrics', { username })
  const cached = getFromCache(cacheKey)
  if (cached) return cached

  try {
    // For now, return estimated metrics based on mentions data
    // In the future, we can create a dedicated backend endpoint for user metrics
    const mentions = await fetchLunaticsMentions(100)
    const userMentions = mentions.filter(mention => mention.username === username)
    
    const totalEngagement = userMentions.reduce((sum, mention) => 
      sum + mention.likes + mention.retweets, 0
    )

    const metrics = {
      followers: Math.floor(Math.random() * 5000) + 1000, // Estimated
      engagement: totalEngagement,
      mentions: userMentions.length
    }

    setCache(cacheKey, metrics)
    return metrics

  } catch (error) {
    console.error('Error fetching user metrics:', error)
    return {
      followers: Math.floor(Math.random() * 5000) + 1000,
      engagement: Math.floor(Math.random() * 100) + 50,
      mentions: Math.floor(Math.random() * 50) + 10
    }
  }
}

// Calculate social score based on Twitter metrics
export const calculateSocialScore = (metrics: {
  followers: number
  engagement: number
  mentions: number
}) => {
  const followersScore = Math.min(metrics.followers / 100, 1000) // Max 1000 points
  const engagementScore = metrics.engagement * 10 // 10 points per engagement
  const mentionsScore = metrics.mentions * 50 // 50 points per mention

  return Math.round(followersScore + engagementScore + mentionsScore)
}

// Rate limiting helper
export const rateLimitStatus = {
  remaining: 15,
  resetTime: Date.now() + 15 * 60 * 1000 // 15 minutes
}

export const checkRateLimit = () => {
  if (Date.now() > rateLimitStatus.resetTime) {
    rateLimitStatus.remaining = 15
    rateLimitStatus.resetTime = Date.now() + 15 * 60 * 1000
  }
  return rateLimitStatus.remaining > 0
}

// Helper to check if we're using real data
export const isUsingRealData = () => {
  return ENABLE_REAL_DATA && !!BEARER_TOKEN
} 